import { SelectedUsers, theme } from '@zappyrent/hundop';
import { AxiosError, AxiosResponse } from 'axios';
import { isEmpty, isNil, upperFirst } from 'lodash';
import { ChevronLeft } from 'lucide-react';
import { rem } from 'polished';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ListingAddAnOwner from '../../components/Listings/ListingAddAnOwner';
import { IListingAddAnOwnerForm } from '../../components/Listings/ListingAddAnOwner/ListingAddAnOwner.types';
import ListingCadastralDateForm from '../../components/Listings/ListingCadastralDataForm';
import ListingCard from '../../components/Listings/ListingCard';
import ListingDetailForm from '../../components/Listings/ListingDetailForm';
import ListingDocuments from '../../components/Listings/ListingDocuments';
import { MainLayout } from '../../components/MainLayout';
import { useToastContext } from '../../contexts/ToastProvider';
import listingsAPI from '../../services/api/listings';
import {
  CONTRACT_TYPE,
  EXPENSES,
  RENTAL_AGREEMENT,
  YES_AND_NO,
  TYPE_OF_TAX,
  LANDLORD_REASON,
  PROPERTY_TYPE,
} from '../../shared/constants';
import { useUser } from '../../shared/hooks';
import { IListing } from '../../shared/ts/listing';
import { IOwner } from '../../shared/ts/owner';
import useStore from '../../store';
import { PaginatedResponse } from '../../types/utils';
import {
  StyledAddDocumentTitle,
  StyledAddOwnerCta,
  StyledAddOwnerCtaIcon,
  StyledAddOwnerCtaLabel,
  StyledAddOwnerTitle,
  StyledAddOwnerWrapper,
  StyledBackTitle,
  StyledBackTitleWrapper,
  StyledButton,
  StyledCadastralDateTitle,
  StyledContractInfoTitle,
  StyledIntroWrapper,
  StyledListings,
  StyledMissingFieldLabel,
  StyledMissingFieldLabelsContainer,
  StyledMissingFieldTitle,
  StyledMissingFieldWrapper,
  StyledMissingFieldsText,
  StyledSpinner,
  StyledSubInfo,
  StyledSubInfoSpan,
  StyledSubmitButtonWrapper,
  StyledSubmitCtaButton,
  StyledTitle,
  StyledTrashCan,
} from './ListingDetail.styles';
import { IListingForm, IMissingInformation } from './ListingDetail.types';
import { getDropdownValueFromApi } from './utils/getDropdownValueFromApi';
import { mapExpenseValueFromApi } from './utils/mapExpenseValueFromApi';

const ListingDetail = (): JSX.Element | null => {
  const toast = useToastContext();
  const user = useStore(state => state.user);
  const isLoadingUser = useUser();
  const userType = user?.type;
  const { t } = useTranslation(['listingDetail', 'routes', 'errors']);
  const { locale, listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState<IListing>({} as IListing);
  const [listingOwners, setListingOwners] = useState<IOwner[]>([]);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [ownerDeleteIndex, setOwnerDeleteIndex] = useState<number | null>(null);
  const [owners, setOwners] = useState<IListingAddAnOwnerForm[]>([]);
  const [missingHouseInformation, setMissingHouseInformation] =
    useState<IMissingInformation | null>(null);
  const [missingCadastralInformation, setMissingCadastralInformation] =
    useState<IMissingInformation | null>(null);
  const [missingDocumentInformation, setMissingDocumentInformation] =
    useState<IMissingInformation | null>(null);
  const [missingOwnerInformation, setMissingOwnerInformation] = useState<
    IMissingInformation[]
  >([]);

  const { control, watch, setValue, handleSubmit, reset, formState } =
    useForm<IListingForm>({
      mode: 'onSubmit',
    });
  const values = watch();
  const ownersValues = values?.owners ?? [];

  const { isLoading: isLoadingListingDetail, refetch: refetchListingDetail } =
    useQuery({
      queryKey: ['listing-detail', listingId],
      queryFn: () => listingsAPI.getListingDetail(Number(listingId)),
      onSuccess: (
        response: AxiosResponse<PaginatedResponse<Array<IListing>>>,
      ) => {
        setListing(response.data.data[0]);
      },
      onError(error: AxiosError) {
        console.error(error);
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  const { isLoading: isLoadingListingOwners, refetch: refetchListingOwners } =
    useQuery({
      queryKey: ['listing-owners', listingId],
      queryFn: () => listingsAPI.getListingOwners(Number(listingId)),
      onSuccess: (
        response: AxiosResponse<PaginatedResponse<Array<{ user: IOwner }>>>,
      ) => {
        const owners = response.data.data.map(owner => owner.user);
        setListingOwners(owners ?? []);
      },
      onError(error: AxiosError) {
        console.error(error);
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (!isNil(listing)) {
      const expenses = {
        acqua: listing?.contract_water,
        gas: listing?.contract_gas,
        elettricità: listing?.contract_electricity,
        tari: listing?.contract_garbage,
        condominio: listing?.contract_condominium,
      };
      const owners = listingOwners.map(owner => ({
        user_id: owner.id,
        first_name: owner.name.first_name,
        last_name: owner.name.last_name,
        date_of_birth: owner?.date_of_birth,
        city: owner?.city,
        id_document_number: owner?.id_document?.number,
        tax_code: owner?.tax_code,
        id_document_expiry_date: owner?.id_document?.expiry_date,
      }));
      setOwners(owners);
      const defaultValues = Object.keys(expenses).reduce<{
        [x: string]: string[];
      }>(
        (acc, key) => {
          const val = expenses[key as keyof typeof expenses] as number;
          if (val === 1) {
            acc.selectedInRent.push(key);
          } else if (val === 0) {
            acc.selectedInTenant.push(key);
          } else if (val === 2) {
            acc.selectedInLandlord.push(key);
          }
          return acc;
        },
        { selectedInRent: [], selectedInTenant: [], selectedInLandlord: [] },
      );
      reset({
        contract_type: getDropdownValueFromApi(
          CONTRACT_TYPE,
          listing?.contract_type,
        ),
        rental_agreement: getDropdownValueFromApi(
          RENTAL_AGREEMENT,
          listing?.rental_agreement,
        ),
        contract_tax_type_flat: getDropdownValueFromApi(
          TYPE_OF_TAX,
          listing?.contract_tax_type_flat,
        ),
        pets_allowed: getDropdownValueFromApi(
          YES_AND_NO,
          isNil(listing?.pets_allowed)
            ? undefined
            : listing?.pets_allowed === 'on'
            ? YES_AND_NO.YES.value
            : YES_AND_NO.NO.value,
        ),
        allow_students: getDropdownValueFromApi(
          YES_AND_NO,
          listing?.allow_students,
        ),
        contract_landlord_reason: getDropdownValueFromApi(
          LANDLORD_REASON,
          listing?.contract_landlord_reason,
        ),
        cadastral_sheet: listing?.cadastral_sheet,
        cadastral_parcel: listing?.cadastral_parcel,
        cadastral_category: listing?.cadastral_category,
        cadastral_subordinate: listing?.cadastral_subordinate,
        max_number_of_guests: listing?.max_number_of_guests,
        cadastral_income: listing?.cadastral_income,
        tenant_expense: mapExpenseValueFromApi(defaultValues.selectedInTenant),
        landlord_expense: mapExpenseValueFromApi(
          defaultValues.selectedInLandlord,
        ),
        rent_expense: mapExpenseValueFromApi(defaultValues.selectedInRent),
        property_type: getDropdownValueFromApi(
          PROPERTY_TYPE,
          listing?.contract_property_type,
        ),
        owners,
      });
    }
  }, [listing, listingOwners]);

  const { mutateAsync: mutateListingUpdateAsync } = useMutation(
    listingsAPI.updateListing,
  );

  const { mutateAsync: mutateListingCreateOrUpdateOwnerAsync } = useMutation(
    listingsAPI.createOrUpdateOwner,
  );

  const { mutateAsync: mutateListingDeleteOwnerAsync } = useMutation(
    listingsAPI.deleteListingOwner,
    {
      onSuccess: () => {
        toast.success(upperFirst(t('listingDetail:ownerDeletedSuccessfully')));
      },
      onError: () => {
        toast.error(upperFirst(t('errors:error')));
      },
    },
  );

  const addOwner = () => {
    const defaultOwnerValue = {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      city: '',
      id_document_number: '',
      tax_code: '',
      id_document_expiry_date: '',
    };
    setOwners([...owners, defaultOwnerValue]);
    setValue('owners', [...ownersValues, defaultOwnerValue]);
  };

  const removeOwner = async (index: number) => {
    setOwnerDeleteIndex(index);
    if (!isNil(owners[index]?.user_id)) {
      await mutateListingDeleteOwnerAsync({
        property_id: Number(listingId),
        user_id: owners[index]?.user_id as number,
      });
    }

    const updatedOwners = owners.filter((_, i) => i !== index);
    setOwners(updatedOwners);
    setValue('owners', updatedOwners);

    setMissingOwnerInformation(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields.splice(index, 1); // Remove the missing information for the given index
      return updatedFields;
    });

    setOwnerDeleteIndex(null);
  };

  if (isLoadingUser) return null;

  if (userType !== 'landlord') navigate(`/${locale}/${t('routes:profile')}`);

  const handleFormSubmit = async (values: IListingForm) => {
    if (!isEmpty(formState.errors)) {
      toast.error(upperFirst(t('errors:error')));
      return;
    }

    setSubmitLoading(true);
    const listingDataValue = {
      contract_type: (values?.contract_type?.value ?? null) as string,
      contract_tax_type_flat: (values?.contract_tax_type_flat?.value ??
        null) as number,
      rental_agreement: (values?.rental_agreement?.value ?? null) as string,
      pets_allowed: isNil(values?.pets_allowed?.value)
        ? null
        : values?.pets_allowed?.value === YES_AND_NO.YES.value
        ? 'on'
        : 'off',
      allow_students: (values?.allow_students?.value ?? null) as number,
      cadastral_sheet: values?.cadastral_sheet ?? '',
      cadastral_parcel: values?.cadastral_parcel ?? '',
      cadastral_category: values?.cadastral_category ?? '',
      cadastral_subordinate: values?.cadastral_subordinate ?? '',
      contract_landlord_reason:
        values?.contract_type?.value === CONTRACT_TYPE.TEMPORARY.value
          ? (values.contract_landlord_reason?.value as string)
          : null,
      max_number_of_guests: isNil(values?.max_number_of_guests)
        ? 0
        : Number(values?.max_number_of_guests),
      cadastral_income: isNil(values?.cadastral_income)
        ? 0
        : Number(values?.cadastral_income),
      contract_water: values?.rent_expense?.some(
        item => item.value === EXPENSES.WATER.value,
      )
        ? 1
        : values?.tenant_expense?.some(
            item => item.value === EXPENSES.WATER.value,
          )
        ? 0
        : values?.landlord_expense?.some(
            item => item.value === EXPENSES.WATER.value,
          )
        ? 2
        : null,
      contract_gas: values?.rent_expense?.some(
        item => item.value === EXPENSES.GAS.value,
      )
        ? 1
        : values?.tenant_expense?.some(
            item => item.value === EXPENSES.GAS.value,
          )
        ? 0
        : values?.landlord_expense?.some(
            item => item.value === EXPENSES.GAS.value,
          )
        ? 2
        : null,
      contract_electricity: values?.rent_expense?.some(
        item => item.value === EXPENSES.ELECTRICITY.value,
      )
        ? 1
        : values?.tenant_expense?.some(
            item => item.value === EXPENSES.ELECTRICITY.value,
          )
        ? 0
        : values?.landlord_expense?.some(
            item => item.value === EXPENSES.ELECTRICITY.value,
          )
        ? 2
        : null,
      contract_condominium: values?.rent_expense?.some(
        item => item.value === EXPENSES.CONDOMINIO.value,
      )
        ? 1
        : values?.tenant_expense?.some(
            item => item.value === EXPENSES.CONDOMINIO.value,
          )
        ? 0
        : values?.landlord_expense?.some(
            item => item.value === EXPENSES.CONDOMINIO.value,
          )
        ? 2
        : null,
      contract_garbage: values?.rent_expense?.some(
        item => item.value === EXPENSES.GARBAGE.value,
      )
        ? 1
        : values?.tenant_expense?.some(
            item => item.value === EXPENSES.GARBAGE.value,
          )
        ? 0
        : values?.landlord_expense?.some(
            item => item.value === EXPENSES.GARBAGE.value,
          )
        ? 2
        : null,
      contract_property_type: values?.property_type?.value as string,
    };

    try {
      await mutateListingUpdateAsync({
        property_id: Number(listingId),
        payload: listingDataValue,
      });

      if (!isEmpty(values?.owners) && values?.owners) {
        await Promise.all(
          values.owners?.map(async owner => {
            await mutateListingCreateOrUpdateOwnerAsync({
              property_id: Number(listingId),
              payload: owner,
            });
          }),
        );
      }
      refetchListingDetail();
      refetchListingOwners();
      toast.success(
        upperFirst(t('listingDetail:listingDetailFormSubmittedSuccessfully')),
      );
      setSubmitLoading(false);
    } catch (error) {
      toast.error(
        upperFirst(t('listingDetail:listingDetailFormSubmittedError')),
      );
      setSubmitLoading(false);
    }
  };

  const handleMissingOwnerInformation = (
    index: number,
    missingFields: IMissingInformation,
  ) => {
    setMissingOwnerInformation(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields[index] = missingFields;
      // Filter out arrays with non-zero length
      return updatedFields.filter(field => !isEmpty(field.fields));
    });
  };

  return (
    <MainLayout>
      <StyledIntroWrapper>
        <div>
          <StyledBackTitleWrapper
            onClick={() => {
              navigate(`/${locale}/${t('navigation:listings')}`);
            }}
          >
            <ChevronLeft
              width={rem('16px')}
              height="auto"
              color={theme.colors.landlord.main}
            />
            <StyledBackTitle>
              {upperFirst(t('listingDetail:backToListingPage'))}
            </StyledBackTitle>
          </StyledBackTitleWrapper>
          <StyledTitle>
            {upperFirst(t('listingDetail:houseRequirements'))}
          </StyledTitle>
          <StyledSubInfo>
            <Trans
              components={{
                1: <StyledSubInfoSpan />,
              }}
              i18nKey="listingDetail:addInfoTGenerateContract"
              t={t}
            />
          </StyledSubInfo>
        </div>

        {isLoadingListingDetail && isLoadingListingOwners ? (
          <StyledSpinner
            foregroundColor={theme.colors.landlord.main}
            size={15}
            strokeSize={2}
          />
        ) : (
          <div></div>
        )}
      </StyledIntroWrapper>

      <StyledListings>
        <ListingCard
          id={listing.id}
          name={listing.name ?? listing.address}
          status={listing.status}
          price={listing.price}
          size={listing.size}
          nBedrooms={listing.total_bedrooms}
          nBathrooms={listing.bathrooms}
          image={listing.img_desktop_search}
          showButtons={false}
          province={listing.province}
        />
      </StyledListings>

      {!isNil(missingHouseInformation) ||
      !isNil(missingCadastralInformation) ||
      !isNil(missingDocumentInformation) ||
      !isEmpty(missingOwnerInformation) ? (
        <StyledMissingFieldWrapper>
          <StyledMissingFieldTitle>
            {upperFirst(t('listingDetail:missingInfo'))}:
          </StyledMissingFieldTitle>

          {missingHouseInformation && (
            <StyledMissingFieldLabelsContainer>
              <StyledMissingFieldLabel>
                {upperFirst(t(missingHouseInformation.label))}:
              </StyledMissingFieldLabel>
              {missingHouseInformation.fields.map((field, index) => (
                <StyledMissingFieldsText key={`missing-field-${index}`}>
                  {upperFirst(t(field))}
                </StyledMissingFieldsText>
              ))}
            </StyledMissingFieldLabelsContainer>
          )}

          {missingCadastralInformation && (
            <StyledMissingFieldLabelsContainer>
              <StyledMissingFieldLabel>
                {upperFirst(t(missingCadastralInformation.label))}:
              </StyledMissingFieldLabel>
              {missingCadastralInformation.fields.map((field, index) => (
                <StyledMissingFieldsText key={`missing-field-${index}`}>
                  {upperFirst(t(field))}
                </StyledMissingFieldsText>
              ))}
            </StyledMissingFieldLabelsContainer>
          )}

          {missingDocumentInformation && (
            <StyledMissingFieldLabelsContainer>
              <StyledMissingFieldLabel>
                {upperFirst(t(missingDocumentInformation.label))}:
              </StyledMissingFieldLabel>
              {missingDocumentInformation.fields.map((field, index) => (
                <StyledMissingFieldsText key={`missing-field-${index}`}>
                  {upperFirst(t(field))}
                </StyledMissingFieldsText>
              ))}
            </StyledMissingFieldLabelsContainer>
          )}

          {missingOwnerInformation.map((field, index) => (
            <StyledMissingFieldLabelsContainer key={`missing-field-${index}`}>
              <StyledMissingFieldLabel>
                {upperFirst(t(field.label))}:
              </StyledMissingFieldLabel>
              {field.fields.map((field, index) => (
                <StyledMissingFieldsText key={`missing-field-${index}`}>
                  {upperFirst(t(field))}
                </StyledMissingFieldsText>
              ))}
            </StyledMissingFieldLabelsContainer>
          ))}
        </StyledMissingFieldWrapper>
      ) : (
        <></>
      )}

      <StyledContractInfoTitle>
        {upperFirst(t('listingDetail:contractInfo'))}
      </StyledContractInfoTitle>

      <form
        id="listing-detail-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        style={{ width: '100%' }}
      >
        <ListingDetailForm
          control={control}
          watch={watch}
          setValue={setValue}
          setMissingHouseInformation={setMissingHouseInformation}
        />

        <StyledCadastralDateTitle>
          {upperFirst(t('listingDetail:cadastralInfo'))}
        </StyledCadastralDateTitle>
        <ListingCadastralDateForm
          control={control}
          watch={watch}
          setMissingCadastralInformation={setMissingCadastralInformation}
        />

        <StyledAddDocumentTitle>
          {upperFirst(t('listingDetail:addDocuments'))}
        </StyledAddDocumentTitle>

        <ListingDocuments
          setMissingDocumentInformation={setMissingDocumentInformation}
        />

        <StyledAddOwnerWrapper>
          <StyledAddOwnerTitle>
            {upperFirst(t('listingDetail:addOwner'))}
          </StyledAddOwnerTitle>
          {!!owners.length && (
            <StyledButton
              onClick={() => {
                removeOwner(0);
              }}
              disabled={ownerDeleteIndex === 0}
              type="button"
            >
              <StyledTrashCan />
            </StyledButton>
          )}
        </StyledAddOwnerWrapper>
        {owners.map((__: IListingAddAnOwnerForm, index: number) => (
          <div key={`listing-owner-${index}`}>
            <ListingAddAnOwner
              control={control}
              watch={watch}
              handleMissingOwnerInformation={handleMissingOwnerInformation}
              index={index}
            />
            <StyledAddOwnerWrapper>
              {owners.length <= 4 && index !== owners.length - 1 && (
                <StyledAddOwnerTitle>
                  {upperFirst(t('listingDetail:addOtherOwner'))}
                </StyledAddOwnerTitle>
              )}
              {owners.length > 1 && index !== owners.length - 1 && (
                <StyledButton
                  onClick={e => {
                    e.preventDefault();
                    removeOwner(index + 1);
                  }}
                  disabled={ownerDeleteIndex === index + 1}
                >
                  <StyledTrashCan />
                </StyledButton>
              )}
            </StyledAddOwnerWrapper>
          </div>
        ))}
        {owners.length !== 4 && (
          <StyledAddOwnerCta
            onClick={e => {
              e.preventDefault();
              addOwner();
            }}
          >
            <StyledAddOwnerCtaIcon>
              <SelectedUsers />
            </StyledAddOwnerCtaIcon>
            <StyledAddOwnerCtaLabel>
              {owners.length
                ? upperFirst(t('listingDetail:addOtherOwner'))
                : upperFirst(t('listingDetail:addOwner'))}
            </StyledAddOwnerCtaLabel>
          </StyledAddOwnerCta>
        )}

        <StyledSubmitButtonWrapper>
          <StyledSubmitCtaButton
            user="landlord"
            variant="primary"
            type="submit"
            disabled={submitLoading}
          >
            {submitLoading && (
              <StyledSpinner
                foregroundColor={theme.colors.cross.ultralightGray}
                size={15}
                strokeSize={2}
              />
            )}
            {t('listingDetail:save')}
          </StyledSubmitCtaButton>
        </StyledSubmitButtonWrapper>
      </form>
    </MainLayout>
  );
};

export default ListingDetail;
